"use client"

import { useState, useEffect } from "react"
import { apiFetch } from "@/lib/api/apiFetch"

export interface UserDetails {
  id: string
  name: string
  email: string
  phone?: string
  source?: string
  avatar?: string
  orgId: string
  role: string
  enabled: number
  settings?: {
    notifications?: {
      email: boolean
      push: boolean
      successSound: string
      errorSound: string
    }
  }
  createdAt: string
  updatedAt: string
  street?: string
  city?: string
  state?: string
  country?: string
  zip?: string
  organization?: {
    id: string
    name: string
    orgId: string
    registerNumber: string
    email: string
    phone?: string
    address: string
    domains: string[]
  }
  security?: {
    mfaEnabled: boolean
    totpEnabled: boolean
    passkeyEnabled: boolean
    backupCodesEnabled: boolean
    backupCodes: string[]
    totpVerified: boolean
  }
}

export function useUser() {
  const [user, setUser] = useState<UserDetails | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<any>(null)

  const fetchUserDetails = async () => {
    try {
      setLoading(true)
      const data = await apiFetch("/api/users/details")
      setUser(data.data)
      setError(null)
    } catch (err) {
      console.error("Failed to fetch user details:", err)
      setError(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUserDetails()
  }, [])

  const updateUserSettings = async (settingsPayload: any) => {
    if (!user?.id) throw new Error("No user ID found")
    try {
      setLoading(true)
      await apiFetch(`/api/users/${user.id}`, {
        method: "PUT",
        data: { settings: settingsPayload }
      })
      await fetchUserDetails()
    } catch (err) {
      console.error("Failed to update user settings:", err)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const updateUser = async (payload: any) => {
    if (!user?.id) throw new Error("No user ID found")
    try {
      setLoading(true)
      await apiFetch(`/api/users/${user.id}`, {
        method: "PUT",
        data: payload
      })
      await fetchUserDetails()
    } catch (err) {
      console.error("Failed to update user:", err)
      throw err
    } finally {
      setLoading(false)
    }
  }

  return { user, loading, error, refetch: fetchUserDetails, updateUserSettings, updateUser }
}
